/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import Typography from "@mui/material/Typography"
import {useAppDispatch, useAppSelector} from "../../../../../redux/hooks"
import {openConnectAdasProgramWizard, selectCurrentStudyId, selectExperiment, selectLoading, selectStudy, selectTable, updateExperiment} from "../../../../../redux/ducks/SensingSystemArchitectureDesign"
import {selectProgram} from "../../../../../redux/ducks/AdasPrograms"
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox"
import Link from "@mui/material/Link"
import {Link as RouterLink} from "react-router-dom"
import ADAS_PROGRAMS_CONSTANTS from "../../../AdasPrograms/constants"
import {SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS, STUDY_ADAS_PROGRAM_NULL} from "../../constants"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import {ArgosPlanOfRecordIcon as PlanOfRecordIcon, ArgosSaveIcon as SaveIcon, ArgosWizardIcon as WizardIcon} from "../../../../components/Icons/ArgosActionIcons"
import {tableButtonGray} from "../../../../../constants/colors"
import ArgosCollapseComponent from "../../../../components/CollapseComponent/ArgosCollapseComponent"
import {BillOfMaterialsSection} from "./BillOfMaterialsSection/BillOfMaterialsSection"
import {SensingSystemComponentAnalysisSection} from "./SensingSystemComponentAnalysisSection/SensingSystemComponentAnalysisSection"
import Loader from "../../../../components/Loader/Loader"
import toast from "react-hot-toast";
import {usePostRequest} from "../../../../../api/helper";
import { ArgosFavoriteIconLogic } from "../../../../components/Buttons/ArgosFavoriteIconLogic"
import { ETrackedItemTypes } from "../../../../../constants/trackedItems"


/**
 * Experiments panel on the Sensing System Architecture Design View Study page
 */
export const ExperimentsPanel = () => {
  const dispatch = useAppDispatch()
  const studyId = useAppSelector(selectCurrentStudyId)
  const study = useAppSelector(state => selectStudy(state, studyId ?? ''))
  const experiment = useAppSelector(selectExperiment)
  const loading = useAppSelector(selectLoading)
  const fetchExperimentsLoading = loading.fetchExperiments
  const isFetchingStudy = useAppSelector(state => selectTable(state).isFetching)
  const adasProgram = useAppSelector(state => selectProgram(state, study?.adasProgramId ?? ''))
  const adasProgramUrl = ADAS_PROGRAMS_CONSTANTS.helperFunctions.formAdasProgramUrl(study?.adasProgramId ?? '')
  const adasProgramIsPlanOfRecord = adasProgram?.data.isPlanOfRecord

  const collapseSections = {
    'Bill of Materials': (
      <BillOfMaterialsSection/>
    ),
    'Sensing System Component Analysis': (
      <SensingSystemComponentAnalysisSection/>
    ),
  }

  const editExperimentUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.editExperiment
  const editExperimentHandle = usePostRequest(editExperimentUrl)
  const handleSaveTable = () => {
    // right now just the row selection
    toast.loading('Saving table...', {id: 'save-table-toast'})
    editExperimentHandle(experiment).then((res: any) => {
      dispatch(updateExperiment(res))
      toast.success('Table saved')
    }).catch((err: any) => {
      console.log(err)
      toast.error('Error saving table')
    }).finally(() => {
      toast.dismiss('save-table-toast')
    })
  }
  /** Favorite button */
  const trackedItemUid = studyId
  const favIcon = <ArgosFavoriteIconLogic
    favoriteType={ETrackedItemTypes.SENSING_SYSTEM_ARCHITECTURE_DESIGN}
    trackedItemUid={trackedItemUid}
    partitionKey={trackedItemUid}
  />
  return (
    <ArgosFlexbox vertical style={{padding: '20px', flexGrow: 1}}>
      <ArgosFlexbox style={{alignItems: 'center', justifyContent: 'space-between'}}>
        <ArgosFlexbox>
          <Typography>
            ADAS Program
          </Typography>
          {study?.adasProgramId && <Link component={RouterLink} to={adasProgramUrl} underline='hover'>
            {adasProgram ? ADAS_PROGRAMS_CONSTANTS.helperFunctions.getFullAdasProgramNameWithNameAndModelYear(adasProgram.data.programName, adasProgram.data.modelYear) : study?.adasProgramId}
          </Link>}
          {study?.adasProgramId && adasProgramIsPlanOfRecord && <Tooltip title='ADAS Program is Plan of Record'>
            <span>
              <PlanOfRecordIcon style={{color: tableButtonGray}}/>
            </span>
          </Tooltip>}
          {!study?.adasProgramId && <Typography>
            {STUDY_ADAS_PROGRAM_NULL}
          </Typography>}
        </ArgosFlexbox>
        <ArgosFlexbox>
          <Tooltip title={study?.adasProgramId ? 'An ADAS Program has already been linked' : 'Connect ADAS Program'}>
            <div>
            <IconButton
              disabled={!!study?.adasProgramId || study?.archive}
              onClick={() => dispatch(openConnectAdasProgramWizard({
                data: {
                  adasProgramId: study?.adasProgramId ?? '',
                  objects: {data: null, isFetching: false},
                  dataSets: {data: null, isFetching: false, selectedDataSet: experiment?.data.redlineDataSetId ?? ''}
                }
              }))}
            >
              <WizardIcon color={tableButtonGray}/>
            </IconButton>
            </div>
          </Tooltip>
          <Tooltip title='Save'>
            <IconButton disabled={study?.archive} onClick={handleSaveTable}>
              <SaveIcon/>
            </IconButton>
          </Tooltip>
          {favIcon}
        </ArgosFlexbox>
      </ArgosFlexbox>
      <Loader isLoading={fetchExperimentsLoading || isFetchingStudy}>
        {study?.adasProgramId ?
          <ArgosCollapseComponent
            data={collapseSections}
            defaultOpen={true}
          />
          :
          <ArgosFlexbox vertical style={{height: '100%', width: '100%', justifyContent: 'center', alignItems: 'center', flexGrow: 1}}>
            <Typography style={{textAlign: 'center'}} fontSize={24}>
              Connect an ADAS Program to get started
            </Typography>
          </ArgosFlexbox>
        }
      </Loader>
    </ArgosFlexbox>
  )
}
